import { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Paper,
  CircularProgress,
  Alert,
} from '@mui/material';
const loginPageImage = `${process.env.PUBLIC_URL || ''}/images/loginpageimage1.webp`;
import rightLoginLogo from '../assets/logos/right_login_logo.png';
import { useAuth } from '../contexts/AuthContext';
import API_CONFIG from '../config/api';

const POPUP_DIMENSIONS = {
  width: 500,
  height: 600,
};
const ALLOWED_ORIGINS = [
  API_CONFIG.BASE_URL,
  window.location.origin,
  'https://youphoria.youngsoft.in',
];
const STORAGE_KEYS = {
  TOKEN: 'token',
  USER_NAME: 'userName',
  USER_PICTURE: 'userPicture',
  USER_EMAIL: 'userEmail',
  GOOGLE_ID: 'googleId',
};
const Login = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [imageLoaded, setImageLoaded] = useState(false);

  useEffect(() => {
    if (sessionStorage.getItem(API_CONFIG.SESSION_TIMEOUT_KEY)) {
      sessionStorage.removeItem(API_CONFIG.SESSION_TIMEOUT_KEY);
      setError('Session timeout. Please log in again.');
    }
  }, []);

  const clearAuthData = useCallback(() => {
    Object.values(STORAGE_KEYS).forEach(key => localStorage.removeItem(key));
  }, []);
  const generateOAuthState = useCallback(() => {
    return btoa(
      JSON.stringify({
        timestamp: new Date().getTime(),
        random: Math.random().toString(36).substring(7),
      })
    );
  }, []);
  const handleGoogleLogin = useCallback(() => {
    setLoading(true);
    setError('');
    clearAuthData();
    const { width, height } = POPUP_DIMENSIONS;
    const left = window.screenX + (window.outerWidth - width) / 2;
    const top = window.screenY + (window.outerHeight - height) / 2;
    const state = generateOAuthState();
    const popup = window.open(
      `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.AUTH.GOOGLE_OAUTH}?state=${state}&prompt=select_account`,
      'Google Login',
      `width=${width},height=${height},left=${left},top=${top}`
    );
    if (!popup) {
      setError('Please allow popups for this website');
      setLoading(false);
      return;
    }

    const messageHandler = async event => {
      if (
        !event.origin.includes('localhost') &&
        !ALLOWED_ORIGINS.includes(event.origin)
      ) {
        return;
      }
      try {
        const data = event.data;
        if (data.error) {
          setError(data.error);
          popup.close();
          window.removeEventListener('message', messageHandler);
          setLoading(false);
          return;
        }
        if (data.token) {
          Object.entries({
            [STORAGE_KEYS.TOKEN]: data.token,
            [STORAGE_KEYS.USER_NAME]: data.name,
            [STORAGE_KEYS.USER_PICTURE]: data.picture,
            [STORAGE_KEYS.USER_EMAIL]: data.email,
            ...(data.googleId && { [STORAGE_KEYS.GOOGLE_ID]: data.googleId }),
            ...(data.id && { user_id: data.id }),
          }).forEach(([key, value]) => localStorage.setItem(key, value));

          await login(data.email, null, true);
          popup.close();
          window.removeEventListener('message', messageHandler);
          setLoading(false);
          // Return to the page the user was headed to (e.g. an email deep link),
          // else the dashboard.
          const redirectTo = sessionStorage.getItem('postLoginRedirect');
          sessionStorage.removeItem('postLoginRedirect');
          navigate(redirectTo || '/dashboard');
        }
      } catch (error) {
        setError('Failed to complete Google login. Please try again.');
        popup.close();
        window.removeEventListener('message', messageHandler);
        setLoading(false);
      }
    };
    window.addEventListener('message', messageHandler);

    const checkPopup = setInterval(() => {
      try {
        if (popup.closed) {
          clearInterval(checkPopup);
          window.removeEventListener('message', messageHandler);
          setLoading(false);
        }
      } catch (error) {
        console.error('Error checking popup:', error);
      }
    }, 1000);
  }, [clearAuthData, generateOAuthState, login, navigate]);

  return (
    <Box
      className="login-page-container"
      sx={{
        display: 'flex',
        flexDirection: { xs: 'column', lg: 'row' },
        justifyContent: 'start',
        alignItems: { xs: 'stretch', lg: 'center' },
        minHeight: '100dvh',
        overflowY: { xs: 'auto', lg: 'hidden' },
        overflowX: 'hidden',
        backgroundColor: { xs: '#f1f5f9', lg: 'transparent' },
      }}
    >
      <Box
        width={{ xs: '100%', lg: '58%' }}
        height={{ xs: '50vh', sm: '52vh', lg: '100dvh' }}
        component="img"
        src={loginPageImage}
        alt="Login Page Image"
        loading="eager"
        fetchPriority="high"
        decoding="async"
        onLoad={() => setImageLoaded(true)}
        sx={{
          objectFit: 'cover',
          objectPosition: 'center',
          backgroundColor: '#f1f5f9',
          opacity: imageLoaded ? 1 : 0,
          transition: 'opacity 0.3s ease-in-out',
        }}
      />

      <Box
        sx={{
          width: { xs: '100%', lg: '42%' },
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: { xs: '50vh', lg: '100%' },
          flexDirection: 'column',
          gap: 2,
          px: { xs: 2, sm: 3, lg: 4 },
          py: { xs: 3, sm: 4, lg: 0 },
          mt: 0,
          position: 'relative',
          zIndex: 2,
          backgroundColor: { xs: '#f1f5f9', lg: 'transparent' },
        }}
      >
        <Paper
          elevation={0}
          sx={{
            padding: { xs: 3, sm: 4, lg: 6 },
            width: '100%',
            maxWidth: { xs: 360, lg: 390 },
            backgroundColor: 'white',
            borderRadius: 3,
            position: 'relative',
            border: '1px solid #E2E8F080',
            boxShadow: '0px 32px 64px -16px #0000001A',
            backdropFilter: 'blur(12px)',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              mb: { xs: 1, lg: 3 },
            }}
          >
            <Box
              component="img"
              src={rightLoginLogo}
              alt="Youngsoft Right Login Logo"
              sx={{
                width: { xs: 120, lg: 200 },
                height: 'auto',
                maxHeight: { xs: 50, lg: 60 },
                objectFit: 'contain',
              }}
              onError={e => {
                e.target.style.display = 'none';
                e.target.nextSibling.style.display = 'flex';
              }}
            />
          </Box>

          <Typography
            variant="h5"
            sx={{
              fontSize: { xs: '1.25rem', sm: '1.6rem', lg: '1.8rem' },
              fontWeight: 'bold',
              textAlign: 'center',
              mb: 1,
              color: '#1e293b',
            }}
          >
            Welcome
          </Typography>

          <Typography
            variant="body2"
            sx={{
              fontSize: { xs: '0.75rem', sm: '0.85rem', lg: '0.9rem' },
              textAlign: 'center',
              color: '#64748b',
              mb: { xs: 0.5, sm: 1, lg: 2 },
            }}
          >
            Sign in with your YSI email account
            <br />
            to access the portal
          </Typography>

          {error && (
            <Alert
              severity="error"
              sx={{
                p: 1,
                mb: { xs: 0.5, sm: 1, lg: 2 },
                fontSize: { xs: '0.75rem', sm: '0.85rem', lg: '0.9rem' },
                '& .MuiAlert-icon': {
                  p: 0,
                  mr: 0.5,
                },
                '& .MuiAlert-message': {
                  p: 0,
                },
              }}
            >
              {error}
            </Alert>
          )}

          <Button
            fullWidth
            variant="outlined"
            onClick={handleGoogleLogin}
            disabled={loading}
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 2,
              padding: '8px 16px',
              fontWeight: 500,
              borderRadius: '10px',
              borderColor: '#000',
              color: '#3c4043',
              backgroundColor: 'white',
              textTransform: 'none',
              fontSize: '14px',
              fontFamily: 'Google Sans, Roboto, arial, sans-serif',
              '&:hover': {
                backgroundColor: '#f8f9fa',
                borderColor: '#dadce0',
                boxShadow:
                  '0 1px 2px 0 rgba(60,64,67,.30), 0 1px 3px 1px rgba(60,64,67,.15)',
              },
              '&:disabled': {
                backgroundColor: '#f8f9fa',
                opacity: 0.7,
              },
            }}
          >
            {loading ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              <>
                <Box
                  sx={{
                    width: 18,
                    height: 18,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <svg width="18" height="18" viewBox="0 0 24 24">
                    <path
                      fill="#4285F4"
                      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                    />
                    <path
                      fill="#34A853"
                      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                    />
                    <path
                      fill="#FBBC05"
                      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                    />
                    <path
                      fill="#EA4335"
                      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                    />
                  </svg>
                </Box>
                <Typography
                  component="span"
                  sx={{ fontSize: '14px', fontWeight: 500 }}
                >
                  {loading ? 'Signing in...' : 'Sign in with Google'}
                </Typography>
              </>
            )}
          </Button>
        </Paper>
        <Box>
          <Typography
            style={{
              fontFamily: 'Inter',
              fontWeight: 400,
              fontStyle: 'Regular',
              fontSize: '10px',
              lineHeight: '15px',
              letterSpacing: '1.5px',
              textAlign: 'center',
              verticalAlign: 'middle',
              color: '#94A3B8',
            }}
          >
            © 2026 Youngsoft India • Portal V1.0
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default Login;
